/**
 * "The land answers" (spec §D.10), the pure part (owner B5): which contour pieces light up around a
 * destination, and how strongly.
 *
 *   const idx = indexContours(geo.contours);                 // once (≈ 100 k vertices → chunks with bboxes)
 *   const lit = buildLitContours(idx, at, { zoom, radiusPx }); // at select t = 0: a few ms
 *
 * 1. The level of detail shown at the arrival zoom (metro when z ≥ 12, otherwise region), plus the ridge.
 * 2. Lines are split into edges (long edges subdivided to R / 8) and an edge is kept when its midpoint lies
 *    within R = radiusPx × m/px(z) of the destination.
 * 3. Each kept edge gets w = smoothstep(R, 0.35·R, d). Consecutive edges with the same quantised weight
 *    are merged into one LineString, so the gradient is smooth, joins stay round inside a run, and no two
 *    translucent caps overlap (runs end with butt caps).
 */
import type { Feature, FeatureCollection, LineString } from "geojson";
import type { ContourCollection, LngLat } from "@/contracts/data";
import { metresPerPixel, smoothstep } from "./geometry";

export type ContourLod = "region" | "metro";

interface Chunk {
  lod: ContourLod;
  ridge: boolean;
  major: boolean;
  /** The line (not copied) and the vertex range [i0, i1]; consecutive chunks share their boundary vertex. */
  line: ReadonlyArray<readonly number[]>;
  i0: number;
  i1: number;
  w: number;
  s: number;
  e: number;
  n: number;
}

export interface ContourIndex {
  chunks: Chunk[];
  vertices: number;
}

export interface LitProps {
  w: number; //      0…1 weight (line-opacity)
  major: boolean; // width = (major ? majorWidth : minorWidth) + 0.4
}

export interface LitBuild {
  data: FeatureCollection<LineString, LitProps>;
  /** Edges (after subdivision) that were kept. */
  segments: number;
  lod: ContourLod;
  radiusM: number;
}

/** Arrival zoom → level of detail shown there (§D.6: the metro lines take over across 11.5–12.5). */
export const litLod = (zoom: number): ContourLod => (zoom >= 12 ? "metro" : "region");

const CHUNK = 48;
const QUANT = 20; // weight steps (0.05)

export function indexContours(fc: ContourCollection | null | undefined, chunkEdges = CHUNK): ContourIndex {
  const chunks: Chunk[] = [];
  let vertices = 0;
  if (!fc) return { chunks, vertices };
  for (const f of fc.features) {
    const p = f.properties;
    const lines = f.geometry.type === "LineString" ? [f.geometry.coordinates] : f.geometry.coordinates;
    for (const line of lines) {
      vertices += line.length;
      for (let i0 = 0; i0 < line.length - 1; i0 += chunkEdges) {
        const i1 = Math.min(line.length - 1, i0 + chunkEdges);
        let w = Infinity;
        let s = Infinity;
        let e = -Infinity;
        let n = -Infinity;
        for (let i = i0; i <= i1; i++) {
          const x = line[i][0];
          const y = line[i][1];
          if (x < w) w = x;
          if (x > e) e = x;
          if (y < s) s = y;
          if (y > n) n = y;
        }
        chunks.push({ lod: p.lod, ridge: p.ridge === true, major: p.major, line, i0, i1, w, s, e, n });
      }
    }
  }
  return { chunks, vertices };
}

const EMPTY: FeatureCollection<LineString, LitProps> = { type: "FeatureCollection", features: [] };

export function buildLitContours(idx: ContourIndex, at: LngLat, o: { zoom: number; radiusPx: number }): LitBuild {
  const lod = litLod(o.zoom);
  const lat0 = at[1];
  const lng0 = at[0];
  const R = Math.max(1, o.radiusPx) * metresPerPixel(lat0, o.zoom);
  const kx = 111320 * Math.cos((lat0 * Math.PI) / 180);
  const ky = 110574;
  const dLng = R / kx;
  const dLat = R / ky;
  const R2 = R * R;
  const inner = 0.35 * R;
  const maxPiece = R / 8;

  const features: Feature<LineString, LitProps>[] = [];
  let segments = 0;

  for (const c of idx.chunks) {
    if (c.lod !== lod && !c.ridge) continue;
    if (c.e < lng0 - dLng || c.w > lng0 + dLng || c.n < lat0 - dLat || c.s > lat0 + dLat) continue;
    const L = c.line;
    const major = c.major || c.ridge;
    let run: number[] | null = null; // flat lng/lat of the open run
    let runW = -1;
    const close = (): void => {
      if (run && run.length >= 4) {
        const coords: [number, number][] = [];
        for (let k = 0; k < run.length; k += 2) coords.push([run[k], run[k + 1]]);
        features.push({ type: "Feature", properties: { w: runW, major }, geometry: { type: "LineString", coordinates: coords } });
      }
      run = null;
      runW = -1;
    };
    for (let i = c.i0; i < c.i1; i++) {
      const A = L[i];
      const B = L[i + 1];
      // local metres, destination at the origin
      const ax = (A[0] - lng0) * kx;
      const ay = (A[1] - lat0) * ky;
      const bx = (B[0] - lng0) * kx;
      const by = (B[1] - lat0) * ky;
      const len = Math.hypot(bx - ax, by - ay);
      // Quick reject: every piece's midpoint is at least |edge midpoint| − len/2 from the destination.
      if (Math.hypot((ax + bx) / 2, (ay + by) / 2) - len / 2 >= R) {
        close();
        continue;
      }
      const n = len > maxPiece ? Math.ceil(len / maxPiece) : 1;
      for (let j = 0; j < n; j++) {
        const t0 = j / n;
        const t1 = (j + 1) / n;
        const mx = ax + (bx - ax) * ((t0 + t1) / 2);
        const my = ay + (by - ay) * ((t0 + t1) / 2);
        const d2 = mx * mx + my * my;
        if (d2 >= R2) {
          close();
          continue;
        }
        const d = Math.sqrt(d2);
        const w = Math.round(smoothstep(R, inner, d) * QUANT) / QUANT;
        if (w <= 0) {
          close();
          continue;
        }
        segments++;
        const x0 = A[0] + (B[0] - A[0]) * t0;
        const y0 = A[1] + (B[1] - A[1]) * t0;
        const x1 = A[0] + (B[0] - A[0]) * t1;
        const y1 = A[1] + (B[1] - A[1]) * t1;
        if (run && runW === w) {
          run.push(x1, y1);
        } else {
          // A new run starts where the previous one ended, so the line has no gap at the change.
          close();
          run = [x0, y0, x1, y1];
          runW = w;
        }
      }
    }
    close();
  }
  return { data: features.length ? { type: "FeatureCollection", features } : EMPTY, segments, lod, radiusM: R };
}
